<template>
  <div class="dancer-appbar-root">
    <header class="dancer-appbar">
      <button
        v-if="isLoggedIn"
        type="button"
        class="dancer-icon-btn"
        aria-label="Open menu"
        @click="drawerOpen = true"
      >
        <span class="dancer-hamburger" aria-hidden="true">
          <span /><span /><span />
        </span>
      </button>

      <router-link to="/" exact class="dancer-brand" @click.native="closeAll">
        <img
          v-if="logoUrl && !logoFailed"
          class="dancer-logo"
          :src="logoUrl"
          :alt="logoAlt"
          @error="logoFailed = true"
        />
        <span v-else class="dancer-wordmark">IABF {{ brandYear }}</span>
      </router-link>

      <router-link
        v-if="!isLoggedIn"
        to="/"
        class="dancer-login-link"
      >
        Log In
      </router-link>
      <div v-else class="dancer-account-wrap">
        <button
          type="button"
          class="dancer-account-btn"
          aria-haspopup="true"
          :aria-expanded="accountOpen ? 'true' : 'false'"
          @click="accountOpen = !accountOpen"
        >
          <span class="dancer-account-name">{{ displayName }}</span>
          <span class="dancer-chev" aria-hidden="true">▾</span>
        </button>
        <div v-if="accountOpen" class="dancer-account-menu" role="menu">
          <div class="dancer-account-head">
            <strong>{{ fullName }}</strong>
            <span v-if="email">{{ email }}</span>
          </div>
          <!-- Account + Billing only for current-year registered dancers -->
          <template v-if="showAccountLinks">
            <router-link
              class="dancer-account-item"
              role="menuitem"
              to="/profile"
              @click.native="closeAll"
            >
              Account
            </router-link>
            <router-link
              v-if="!isFaculty"
              class="dancer-account-item"
              role="menuitem"
              to="/billing"
              @click.native="closeAll"
            >
              Billing
            </router-link>
          </template>
          <router-link
            v-if="hasPastFestivalYears"
            class="dancer-account-item"
            role="menuitem"
            to="/history"
            @click.native="closeAll"
          >
            IABF history
          </router-link>
          <button
            type="button"
            class="dancer-account-item dancer-account-item--danger"
            role="menuitem"
            @click="signOut"
          >
            Sign out
          </button>
        </div>
      </div>
    </header>

    <div
      v-if="isLoggedIn && drawerOpen"
      class="dancer-drawer-backdrop"
      @click="drawerOpen = false"
    />
    <aside
      v-if="isLoggedIn"
      class="dancer-drawer"
      :class="{ 'is-open': drawerOpen }"
      aria-label="Main menu"
    >
      <div class="dancer-drawer-head">
        <span class="dancer-wordmark">Menu</span>
        <button type="button" class="dancer-icon-btn" aria-label="Close menu" @click="drawerOpen = false">
          ✕
        </button>
      </div>
      <nav class="dancer-drawer-nav">
        <p class="dancer-drawer-label">Home</p>
        <router-link
          to="/"
          class="dancer-drawer-link"
          active-class="is-active"
          exact
          @click.native="drawerOpen = false"
        >
          Dashboard
        </router-link>
        <router-link
          v-if="showRegisterCta"
          to="/register"
          class="dancer-drawer-link"
          active-class="is-active"
          @click.native="drawerOpen = false"
        >
          Register for IABF {{ registrationYear }}
        </router-link>

        <!-- Full prep/perform nav only for current-year registered dancers -->
        <template v-if="showFullPrepNav">
          <p class="dancer-drawer-label">Get ready</p>
          <router-link
            v-for="link in prepLinks"
            :key="link.to"
            :to="link.to"
            class="dancer-drawer-link"
            active-class="is-active"
            @click.native="drawerOpen = false"
          >
            {{ link.label }}
          </router-link>

          <template v-if="!isFaculty">
          <p class="dancer-drawer-label">Perform</p>
          <router-link
            to="/pieces"
            class="dancer-drawer-link"
            active-class="is-active"
            @click.native="drawerOpen = false"
          >
            Showcase pieces
          </router-link>
          <router-link
            v-if="showCorpsNav"
            to="/my-pieces"
            class="dancer-drawer-link"
            active-class="is-active"
            @click.native="drawerOpen = false"
          >
            Corps pieces
          </router-link>
          <router-link
            v-else
            to="/group-pieces"
            class="dancer-drawer-link"
            active-class="is-active"
            @click.native="drawerOpen = false"
          >
            Group pieces
          </router-link>
          </template>

          <p class="dancer-drawer-label">Community</p>
          <a
            class="dancer-drawer-link"
            :href="communityForumUrl"
            target="_blank"
            rel="noopener"
            @click="drawerOpen = false"
          >
            Community forum
          </a>
          <a
            v-if="whatsappInviteUrl"
            class="dancer-drawer-link"
            :href="whatsappInviteUrl"
            target="_blank"
            rel="noopener"
            @click="drawerOpen = false"
          >
            WhatsApp group
          </a>
          <span v-else class="dancer-drawer-link is-soon">WhatsApp group · Coming July 2027</span>

          <template v-if="isFaculty">
          <p class="dancer-drawer-label">Shop</p>
          <router-link
            to="/store"
            class="dancer-drawer-link"
            active-class="is-active"
            @click.native="drawerOpen = false"
          >
            Merch store
          </router-link>
          </template>

          <template v-if="!isFaculty">
          <p class="dancer-drawer-label">Resources</p>
          <a
            v-for="link in resourceLinks"
            :key="link.href"
            class="dancer-drawer-link"
            :href="link.href"
            target="_blank"
            rel="noopener"
            @click="drawerOpen = false"
          >
            {{ link.label }}
          </a>
          <router-link
            to="/#stage-makeup"
            class="dancer-drawer-link"
            @click.native="drawerOpen = false"
          >
            Stage makeup tutorial
          </router-link>
          </template>
        </template>

        <p class="dancer-drawer-label">Help</p>
        <router-link
          v-if="hasPastFestivalYears"
          to="/history"
          class="dancer-drawer-link"
          active-class="is-active"
          @click.native="drawerOpen = false"
        >
          IABF history
        </router-link>
        <a
          class="dancer-drawer-link"
          href="https://www.internationaladultballetfestival.com/policies"
          target="_blank"
          rel="noopener"
          @click="drawerOpen = false"
        >
          FAQ &amp; policies
        </a>
      </nav>
    </aside>
  </div>
</template>

<script>
import { getIabfBrand, IABF_BRAND_YEAR } from '@/config/iabf-brand'
import { hasCorpsCostumeOrder, IABF_DANCER_YEAR, IABF_REGISTRATION_YEAR } from '@/config/iabf-event'
import { IABF_COMMUNITY_FORUM_URL } from '@/config/iabf-intro'
import { hasPastFestivalYears as dancerHasPastFestivalYears } from '@/utils/iabf-festival-history'
import { yearsAttendedForDancer } from '@/utils/iabf-attendance-years'
import { auth } from '@/firebase'
import { canSeeDancerPhotoshootSchedule } from '@/config/iabf-photoshoot'
import { IABF_POSING_GUIDE_PDF_URL } from '@/config/iabf-resources'

export default {
  name: 'DancerAppBar',
  data () {
    return {
      drawerOpen: false,
      accountOpen: false,
      logoFailed: false,
      communityForumUrl: IABF_COMMUNITY_FORUM_URL,
      registrationAccess: null,
      photoshootDancerBookingOpen: false
    }
  },
  computed: {
    brand () {
      return getIabfBrand(IABF_BRAND_YEAR)
    },
    brandYear () {
      return this.brand.year
    },
    logoUrl () {
      return this.brand.logoUrl || null
    },
    logoAlt () {
      return this.brand.logoAlt || ('IABF ' + this.brandYear)
    },
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    isLoggedIn () {
      return !!this.$store.getters.user
    },
    whatsappInviteUrl () {
      return this.$store.getters.iabfWhatsappInviteUrl || ''
    },
    displayName () {
      return this.dancer.firstName || 'Account'
    },
    fullName () {
      const f = this.dancer.firstName || ''
      const l = this.dancer.lastName || ''
      return (f + ' ' + l).trim() || 'Account'
    },
    email () {
      return this.dancer.email || (this.$store.getters.user && this.$store.getters.user.email) || ''
    },
    isCanceledAccount () {
      return !!(this.dancer && this.dancer.isCanceledAccount)
    },
    hasPastFestivalYears () {
      const person = this.$store.getters.currentIABFPerson || {}
      const d = this.dancer || {}
      return dancerHasPastFestivalYears({
        yearsAttended: yearsAttendedForDancer(person, d, d.festivalYear || this.enrollmentYear),
        enrollmentYear: d.festivalYear || this.enrollmentYear,
        isCanceledAccount: !!d.isCanceledAccount,
        canceledYears: d.canceledYears
      })
    },
    enrollmentYear () {
      if (this.$store.state.masqueradeDancerId) {
        return Number(this.$store.getters.adminFestivalYear) || IABF_DANCER_YEAR
      }
      return Number(this.$store.getters.iabfDancerYear) || IABF_DANCER_YEAR
    },
    /** Enrollment year older than the current dancer year (e.g. 2026-only alumni). */
    isPriorFestivalYear () {
      if (this.isCanceledAccount) {
        return false
      }
      const y = this.enrollmentYear
      return Number.isFinite(y) && y > 0 && y < IABF_DANCER_YEAR
    },
    /**
     * Full Get ready / Perform / Shop nav only for dancers registered for
     * the current festival year — keeps prior-year accounts from mixing years.
     */
    showFullPrepNav () {
      if (!this.$store.getters.user) {
        return false
      }
      return !this.isCanceledAccount && !this.isPriorFestivalYear
    },
    isFaculty () {
      return !!(this.dancer && (this.dancer.isFaculty || this.dancer.personType === 'faculty'))
    },
    showCorpsNav () {
      return hasCorpsCostumeOrder(this.enrollmentYear)
    },
    canSeePhotoshootSchedule () {
      const email = this.$store.getters.user && this.$store.getters.user.email
      return canSeeDancerPhotoshootSchedule(email, this.photoshootDancerBookingOpen)
    },
    /** Account + Billing are current-year prep — hide for alumni / canceled. */
    showAccountLinks () {
      return this.showFullPrepNav
    },
    registrationYear () {
      return IABF_REGISTRATION_YEAR
    },
    showRegisterCta () {
      if (this.showFullPrepNav) {
        return false
      }
      if (!this.isPriorFestivalYear && !this.isCanceledAccount) {
        return false
      }
      const mode = this.registrationAccess && this.registrationAccess.mode
      return mode === 'public' || mode === 'early'
    },
    prepLinks () {
      const faculty = [
        { to: '/lodging', label: 'Travel & carpool' },
        { to: '/dietary', label: 'Dietary preferences' },
        { to: '/guests', label: 'Your guest' },
        { to: '/community', label: 'IABF intros' },
        { to: '/photoshoot', label: 'Photoshoot edits' }
      ]
      const dancer = [
        { to: '/registration', label: 'Your registration' },
        { to: '/lodging', label: 'Travel & lodging' },
        { to: '/dietary', label: 'Dietary preferences' },
        { to: '/guests', label: 'Guests & meals' },
        { to: '/billing', label: 'Payments' },
        { to: '/photoshoot', label: 'Photoshoot' }
      ]
      const links = this.isFaculty ? faculty : dancer
      if (this.canSeePhotoshootSchedule) {
        return links
      }
      return links.filter((l) => l.to !== '/photoshoot')
    },
    resourceLinks () {
      return [
        {
          label: 'Photoshoot posing guide',
          href: IABF_POSING_GUIDE_PDF_URL
        },
        {
          label: 'How to travel with a tutu',
          href: 'https://app.brocheballetonline.com/m/mOFdaPHI'
        }
      ]
    }
  },
  watch: {
    $route () {
      this.closeAll()
    }
  },
  created () {
    this.$store.dispatch('loadRegistrationAccessGate').then((access) => {
      this.registrationAccess = access || { mode: 'closed' }
    }).catch(() => {
      this.registrationAccess = { mode: 'closed' }
    })
    this.$store.dispatch('loadIabfCommunityLinks').catch(() => {})
    this.$store.dispatch('loadPhotoshootConfig', { useAdminYear: false }).then((config) => {
      this.photoshootDancerBookingOpen = !!(config && config.dancerBookingOpen)
    }).catch(() => {})
  },
  methods: {
    closeAll () {
      this.drawerOpen = false
      this.accountOpen = false
    },
    async signOut () {
      this.closeAll()
      try {
        await auth.signOut()
      } catch (e) { /* ignore */ }
      this.$store.commit('setUser', null)
      this.$store.commit('setCurrentIABFDancer', null)
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.dancer-appbar-root {
  position: sticky;
  top: 0;
  z-index: 100;
}

.dancer-appbar {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  padding: 8px 14px;
  background: var(--iabf-bg-elevated, #fff);
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}

.dancer-brand {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  text-decoration: none;
  color: inherit;
}

.dancer-logo {
  display: block;
  height: 40px;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  object-position: left center;
}

.dancer-wordmark {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 15px;
  font-weight: 400;
  letter-spacing: 0.04em;
  color: var(--iabf-ink, #1c1b1f);
}

.dancer-icon-btn {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--iabf-ink, #1c1b1f);
  font-size: 18px;
  flex-shrink: 0;
}

.dancer-icon-btn:hover {
  background: var(--iabf-bg-soft, #eef0eb);
}

.dancer-hamburger {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 20px;
}

.dancer-hamburger span {
  display: block;
  height: 1.5px;
  background: var(--iabf-ink, #1c1b1f);
  border-radius: 1px;
}

.dancer-login-link {
  flex-shrink: 0;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  font-weight: 600;
  color: var(--iabf-ink, #1c1b1f);
  text-decoration: none;
  padding: 10px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  min-height: 44px;
}

.dancer-login-link:hover {
  background: var(--iabf-bg-accent-soft, #eef0eb);
}

.dancer-account-wrap {
  position: relative;
  flex-shrink: 0;
}

.dancer-account-btn {
  border: none;
  background: transparent;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  font-weight: 600;
  color: var(--iabf-ink, #1c1b1f);
  cursor: pointer;
  padding: 10px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
}

.dancer-account-btn:hover {
  background: var(--iabf-bg-accent-soft, #eef0eb);
}

.dancer-account-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dancer-chev {
  font-size: 11px;
  color: var(--iabf-ink-faint, #8a8792);
}

.dancer-account-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  min-width: 220px;
  background: var(--iabf-bg-elevated, #fff);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  box-shadow: var(--iabf-shadow, 0 8px 24px rgba(0,0,0,.08));
  padding: 8px 0;
  z-index: 50;
}

.dancer-account-head {
  padding: 10px 14px 12px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 4px;
}

.dancer-account-head strong {
  font-size: 15px;
}

.dancer-account-head span {
  font-size: 13px;
  color: var(--iabf-ink-muted, #5c5a63);
}

.dancer-account-item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
  padding: 12px 14px;
  cursor: pointer;
  min-height: 44px;
  text-decoration: none;
}

.dancer-account-item:hover:not(:disabled) {
  background: var(--iabf-bg-soft, #eef0eb);
}

.dancer-account-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.dancer-account-item--danger {
  color: var(--iabf-danger, #b54a4a);
  border-top: 1px solid var(--iabf-border, #e3e0e8);
  margin-top: 4px;
}

.dancer-drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(28, 27, 31, 0.35);
  z-index: 110;
}

.dancer-drawer {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: min(300px, 86vw);
  background: var(--iabf-bg-elevated, #fff);
  z-index: 120;
  transform: translateX(-105%);
  transition: transform 0.2s ease;
  box-shadow: var(--iabf-shadow, 0 8px 24px rgba(0,0,0,.08));
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.dancer-drawer.is-open {
  transform: translateX(0);
}

.dancer-drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 8px 8px 8px 16px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}

.dancer-drawer-nav {
  padding: 12px 10px 24px;
}

.dancer-drawer-label {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  font-weight: 700;
  margin: 14px 8px 6px;
  font-family: var(--iabf-font-body, inherit);
}

.dancer-drawer-link {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  padding: 12px 12px;
  border-radius: 10px;
  cursor: pointer;
  min-height: 48px;
  text-decoration: none;
  box-sizing: border-box;
}

.dancer-drawer-link:hover {
  background: var(--iabf-bg-soft, #eef0eb);
}

.dancer-drawer-link.is-active {
  background: var(--iabf-bg-soft, #eef0eb);
  font-weight: 700;
  box-shadow: inset 3px 0 0 var(--iabf-primary, #8a957d);
}

.dancer-drawer-link.is-soon {
  cursor: default;
  opacity: 0.55;
  pointer-events: none;
}

@media print {
  .dancer-appbar-root {
    display: none !important;
  }
}
</style>
